import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Plus, Edit, Trash2, Dog, MapPin, Package, Check, Search, Calendar, Tag, User, ChevronLeft, ChevronRight, ArrowUpDown, X, Image, CreditCard } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';
import useConfirmation from '../hooks/useConfirmation';
import Portal from '../components/Portal';
import MenuDropdown from '../components/MenuDropdown';
import { useAuth } from '../contexts/AuthContext';
import { usePetsAggregates } from '../utils/usePetsAggregates';
import { updatePetsAggregationOnCreate, updatePetsAggregationOnUpdate, updatePetsAggregationOnDelete } from '../utils/petsAggregations';

const EMPTY_PET_FORM = {
  nome: '',
  raca: '',
  mesNascimento: '',
  anoNascimento: '',
  porte: '',
  pelagem: '',
  tutor: '',
  telefone: '',
  endereco: '',
  observacoes: '',
  fonteContato: '',
  temAlergia: false,
  detalhesAlergia: '',
  temDorArticular: false,
  detalhesDorArticular: '',
  usaMedicamento: false,
  detalhesMedicamento: '',
  acompanhamentoVeterinario: false,
  detalhesAcompanhamento: '',
  autorizaImagem: false
};

const PET_MODAL_MONTH_OPTIONS = [
  { value: '', label: 'Mês' },
  { value: 'Janeiro', label: 'Janeiro' },
  { value: 'Fevereiro', label: 'Fevereiro' },
  { value: 'Março', label: 'Março' },
  { value: 'Abril', label: 'Abril' },
  { value: 'Maio', label: 'Maio' },
  { value: 'Junho', label: 'Junho' },
  { value: 'Julho', label: 'Julho' },
  { value: 'Agosto', label: 'Agosto' },
  { value: 'Setembro', label: 'Setembro' },
  { value: 'Outubro', label: 'Outubro' },
  { value: 'Novembro', label: 'Novembro' },
  { value: 'Dezembro', label: 'Dezembro' }
];

const PET_MODAL_PORTE_OPTIONS = [
  { value: '', label: 'Selecione o porte' },
  { value: 'mini', label: 'Mini - até 4 kg' },
  { value: 'pequeno', label: 'Pequeno - entre 4 kg e 10 kg' },
  { value: 'medio', label: 'Médio - entre 10 kg e 25 kg' },
  { value: 'grande', label: 'Grande - entre 25 kg e 45 kg' },
  { value: 'gigante', label: 'Gigante - acima de 45 kg' }
];

const PET_MODAL_PELAGEM_OPTIONS = [
  { value: '', label: 'Selecione a pelagem' },
  { value: 'curto', label: 'Curto' },
  { value: 'medio', label: 'Médio' },
  { value: 'longo', label: 'Longo' }
];

const PET_LIST_PLAN_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'with-plan', label: 'Plano Ativo' },
  { value: 'plan-ended', label: 'Plano Encerrado' },
  { value: 'without-plan', label: 'Sem Plano' }
];

const PET_LIST_IMAGE_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'with-images', label: 'Com imagens' },
  { value: 'without-images', label: 'Sem imagens' }
];

const Pets = () => {
  const { pets: aggregatedPets, loading: aggregatesLoading } = usePetsAggregates();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [petFieldErrors, setPetFieldErrors] = useState({});
  const [formData, setFormData] = useState(() => ({ ...EMPTY_PET_FORM }));
  const [searchTerm, setSearchTerm] = useState(() => {
    // Carregar do localStorage ao inicializar
    return localStorage.getItem('pets_searchTerm') || '';
  });
  const [planFilter, setPlanFilter] = useState(() => {
    // Carregar do localStorage ao inicializar
    return localStorage.getItem('pets_planFilter') || 'all';
  });
  const [imageFilter, setImageFilter] = useState(() => {
    return localStorage.getItem('pets_imageFilter') || 'all';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    // Carregar do localStorage ao inicializar
    return localStorage.getItem('pets_sortOrder') || 'asc'; // 'asc' = A-Z, 'desc' = Z-A
  });
  const [currentPage, setCurrentPage] = useState(1);
  const petsPerPage = 20; // 5 linhas × 4 cards por linha
  const { confirmation, showConfirmation, hideConfirmation, confirmAction, cancelAction } = useConfirmation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const manualUpdateRef = useRef(false); // Rastrear atualizações manuais do estado
  const petModalSheetRef = useRef(null);
  const petObservationsRef = useRef(null);

  const petModalYearOptions = useMemo(
    () => [
      { value: '', label: 'Ano' },
      ...Array.from({ length: 20 }, (_, i) => {
        const y = new Date().getFullYear() - i;
        return { value: String(y), label: String(y) };
      })
    ],
    []
  );

  const fitPetObservationsTextarea = useCallback(() => {
    const el = petObservationsRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Função helper para converter valor do porte em label
  const getSizeLabel = (size) => {
    const labels = {
      'mini': 'Mini - até 4 kg',
      'pequeno': 'Pequeno - entre 4 kg e 10 kg',
      'medio': 'Médio - entre 10 kg e 25 kg',
      'grande': 'Grande - entre 25 kg e 45 kg',
      'gigante': 'Gigante - acima de 45 kg'
    };
    return labels[size] || size;
  };

  // Função helper para converter valor da pelagem em label
  const getCoatLabel = (coat) => {
    const labels = {
      'curto': 'Curto',
      'medio': 'Médio',
      'longo': 'Longo'
    };
    return labels[coat] || coat;
  };

  // Função para formatar nome com apenas as primeiras letras maiúsculas
  const formatName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Função helper para formatar telefone
  const formatPhone = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Limita a 11 dígitos (máximo para celular brasileiro)
    const limitedNumbers = numbers.slice(0, 11);
    
    // Formata conforme o tamanho
    if (limitedNumbers.length <= 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      return limitedNumbers.replace(/(\d{2})(\d{4})(\d{0,4})/, (match, p1, p2, p3) => {
        if (p3) return `(${p1}) ${p2}-${p3}`;
        if (p2) return `(${p1}) ${p2}`;
        if (p1) return `(${p1}`;
        return limitedNumbers;
      });
    } else {
      // Celular: (XX) XXXXX-XXXX
      return limitedNumbers.replace(/(\d{2})(\d{5})(\d{0,4})/, (match, p1, p2, p3) => {
        if (p3) return `(${p1}) ${p2}-${p3}`;
        if (p2) return `(${p1}) ${p2}`;
        if (p1) return `(${p1}`;
        return limitedNumbers;
      });
    }
  };

  // Atualizar pets quando o agregado for carregado
  useEffect(() => {
    if (!aggregatesLoading) {
      // Se houve uma atualização manual recente, não sobrescrever imediatamente
      // Aguardar um pouco para ver se o agregado foi atualizado
      if (manualUpdateRef.current) {
        // Usar um timeout para verificar se o agregado foi atualizado
        const timeoutId = setTimeout(() => {
          // Verificar se o agregado foi atualizado comparando os tamanhos
          // Se o agregado tem menos pets que o estado local, significa que foi atualizado
          setPets(prevPets => {
            if (aggregatedPets.length < prevPets.length) {
              // O agregado foi atualizado (pet foi removido), usar o agregado
              manualUpdateRef.current = false;
              return aggregatedPets;
            }
            // Manter o estado local se o agregado ainda não foi atualizado
            return prevPets;
          });
        }, 300);
        
        return () => clearTimeout(timeoutId);
      } else {
        // Sem atualização manual, usar o agregado normalmente
        setPets(aggregatedPets);
      }
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [aggregatedPets, aggregatesLoading]);

  // Salvar filtros e pesquisa no localStorage quando mudarem
  useEffect(() => {
    localStorage.setItem('pets_searchTerm', searchTerm);
    setCurrentPage(1); // Resetar página quando busca mudar
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('pets_planFilter', planFilter);
    setCurrentPage(1); // Resetar página quando filtro mudar
  }, [planFilter]);

  useEffect(() => {
    localStorage.setItem('pets_imageFilter', imageFilter);
    setCurrentPage(1); // Resetar página quando filtro mudar
  }, [imageFilter]);

  useEffect(() => {
    localStorage.setItem('pets_sortOrder', sortOrder);
    setCurrentPage(1); // Resetar página quando ordem mudar
  }, [sortOrder]);

  // Atualizar placeholder do campo de pesquisa baseado no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      const searchInput = document.querySelector('input[data-desktop-placeholder]');
      if (searchInput) {
        if (window.innerWidth >= 640) {
          searchInput.placeholder = searchInput.getAttribute('data-desktop-placeholder');
        } else {
          searchInput.placeholder = 'Pesquisar';
        }
      }
    };

    handleResize(); // Executar na montagem
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Travar scroll do fundo quando o modal Novo/Editar Pet estiver aberto
  useEffect(() => {
    if (showModal) {
      const scrollY = window.scrollY;
      const prevHtmlOverflow = document.documentElement.style.overflow;
      const prevBodyOverflow = document.body.style.overflow;
      const prevBodyPosition = document.body.style.position;
      const prevBodyTop = document.body.style.top;
      const prevBodyWidth = document.body.style.width;
      const prevBodyHeight = document.body.style.height;

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';

      return () => {
        document.documentElement.style.overflow = prevHtmlOverflow;
        document.body.style.overflow = prevBodyOverflow;
        document.body.style.position = prevBodyPosition;
        document.body.style.top = prevBodyTop;
        document.body.style.width = prevBodyWidth;
        document.body.style.height = prevBodyHeight;
        window.scrollTo(0, scrollY);
      };
    }
  }, [showModal]);

  // Atualiza formData e limpa erro do(s) campo(s) alterado(s)
  const handlePetFormChange = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setPetFieldErrors(prev => {
      const next = { ...prev };
      Object.keys(updates).forEach(k => delete next[k]);
      return next;
    });
  };

  const closePetModal = useCallback(() => {
    setPetFieldErrors({});
    setShowModal(false);
    setEditingPet(null);
    setFormData({ ...EMPTY_PET_FORM });
  }, []);

  const handlePetMenuChange = (e) => {
    const { name, value } = e.target;
    handlePetFormChange({ [name]: value });
  };

  useLayoutEffect(() => {
    if (!showModal) return;
    fitPetObservationsTextarea();
    const onResize = () => fitPetObservationsTextarea();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showModal, formData.observacoes, fitPetObservationsTextarea]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos obrigatórios: Nome do Pet, Nome do Tutor e Telefone
    const errors = {};
    if (!formData.nome?.trim()) errors.nome = true;
    if (!formData.tutor?.trim()) errors.tutor = true;
    const phoneDigits = String(formData.telefone || '').replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length < 10) errors.telefone = true;

    if (Object.keys(errors).length > 0) {
      setPetFieldErrors(errors);
      return;
    }
    setPetFieldErrors({});

    try {
      const petData = {
        ...formData,
        nome: formData.nome.toUpperCase(),
        raca: (formData.raca || '').toUpperCase(),
        tutor: (formData.tutor || '').toUpperCase(),
        planoMensal: formData.planoMensal !== undefined ? formData.planoMensal : false,
        criadoEm: new Date(),
        atualizadoEm: new Date()
      };
      if (!editingPet) {
        petData.galeria = null;
      }

      if (editingPet) {
        // Converter datas para Timestamp
        const petDataWithTimestamp = {
          ...petData,
          criadoEm: editingPet.criadoEm || Timestamp.now(),
          atualizadoEm: Timestamp.now()
        };
        
        await updateDoc(doc(db, 'pets', editingPet.id), petDataWithTimestamp);
        
        // Otimização: usar dados que já temos ao invés de ler do Firestore
        // Converter datas para garantir compatibilidade com Timestamp
        const criadoEmTimestamp = editingPet.criadoEm instanceof Date 
          ? Timestamp.fromDate(editingPet.criadoEm)
          : (editingPet.criadoEm?.toDate ? editingPet.criadoEm : (editingPet.criadoEm || Timestamp.now()));
        
        const updatedPetData = {
          id: editingPet.id,
          ...petDataWithTimestamp,
          criadoEm: criadoEmTimestamp,
          diasAgendados: editingPet.diasAgendados || [],
          planoMensal: petData.planoMensal !== undefined ? petData.planoMensal : (editingPet.planoMensal || false),
          galeria: editingPet.galeria ?? null
        };
        
        // Atualizar agregado com dados que já temos
        try {
          await updatePetsAggregationOnUpdate(updatedPetData);
        } catch (err) {
          // Erro ao atualizar agregado ao editar pet
        }
        
        // O listener em tempo real atualizará automaticamente
        
        // Atualizar o pet no estado local
        setPets(prev => {
          const updated = prev.map(p => {
            if (p.id === editingPet.id) {
              return updatedPetData;
            }
            return p;
          });
          // Reordenar por nome
          return updated.sort((a, b) => {
            const nameA = (a.nome || '').toUpperCase();
            const nameB = (b.nome || '').toUpperCase();
            return nameA.localeCompare(nameB, 'pt-BR');
          });
        });
      } else {
        // Função helper para extrair o primeiro nome
        const getFirstName = (fullName) => {
          return (fullName || '').trim().split(/\s+/)[0].toUpperCase();
        };

        // Verificar se já existe um pet com as mesmas informações (comparação em pares)
        // Usar aggregatedPets diretamente para garantir dados mais atualizados do agregado
        const normalizedName = (petData.nome || '').trim().toUpperCase();
        const normalizedOwnerFirstName = getFirstName(petData.tutor || '');
        const normalizedPhone = (petData.telefone || '').trim();
        
        // Só verificar duplicatas se pelo menos o nome estiver preenchido
        const duplicatePet = normalizedName ? aggregatedPets.find(pet => {
          const nomePet = (pet.nome || '').trim().toUpperCase();
          const petOwnerFirstName = getFirstName(pet.tutor || '');
          const petPhone = (pet.telefone || '').trim();
          
          // Só fazer match se o nome do pet for igual e não vazio
          if (!nomePet || nomePet !== normalizedName) {
            return false;
          }
          
          // Verificar combinação 1: nome pet + telefone (ambos devem estar preenchidos)
          if (normalizedPhone && petPhone) {
            if (petPhone === normalizedPhone) {
              return true;
            }
          }
          
          // Verificar combinação 2: nome pet + primeiro nome do tutor (ambos devem estar preenchidos)
          if (normalizedOwnerFirstName && petOwnerFirstName) {
            if (petOwnerFirstName === normalizedOwnerFirstName) {
              return true;
            }
          }
          
          return false;
        }) : null;

        if (duplicatePet) {
          const matchReason = (() => {
            const nomePet = (duplicatePet.nome || '').trim().toUpperCase();
            const petPhone = (duplicatePet.telefone || '').trim();
            const petOwnerFirstName = getFirstName(duplicatePet.tutor || '');
            
            if (nomePet === normalizedName && petPhone === normalizedPhone) {
              return 'mesmo nome e telefone';
            }
            if (nomePet === normalizedName && petOwnerFirstName === normalizedOwnerFirstName) {
              return 'mesmo nome e tutor';
            }
            return 'informações similares';
          })();

          const confirmed = await showConfirmation({
            title: 'Pet Já Cadastrado',
            message: `Já existe um pet cadastrado com ${matchReason}:\n\nNome: ${duplicatePet.nome}\nTutor: ${duplicatePet.tutor}\nTelefone: ${duplicatePet.telefone ? formatPhone(duplicatePet.telefone) : 'Não informado'}\n\nDeseja mesmo cadastrar novamente?`,
            confirmText: 'Sim, Cadastrar',
            cancelText: 'Cancelar',
            type: 'warning'
          });

          if (!confirmed) {
            return;
          }
        }

        // Novo pet: não incluir planoMensal no Firebase (atualização em tempo real mostrará na lista)
        const { planoMensal: _planoMensal, ...petDataSemPlano } = petData;
        const petDataWithTimestamp = {
          ...petDataSemPlano,
          galeria: null,
          criadoEm: Timestamp.now(),
          atualizadoEm: Timestamp.now()
        };
        
        const docRef = await addDoc(collection(db, 'pets'), petDataWithTimestamp);
        
        const newPetData = {
          id: docRef.id,
          ...petDataWithTimestamp,
          diasAgendados: [] // Pet novo não tem agendamentos
        };
        
        // Atualizar agregado com dados que já temos
        try {
          await updatePetsAggregationOnCreate(newPetData);
        } catch (aggError) {
          // Erro ao atualizar agregado na criação - mostrar erro ao usuário
          await showConfirmation({
            title: 'Aviso',
            message: `Pet cadastrado com sucesso, mas houve um erro ao atualizar o agregado. Erro: ${aggError.message || 'Erro desconhecido'}`,
            confirmText: 'OK',
            cancelText: '',
            type: 'warning'
          });
        }
        
        // O listener em tempo real atualizará a lista automaticamente
      }

      setShowModal(false);
      setEditingPet(null);
      setFormData({ ...EMPTY_PET_FORM });
    } catch (error) {
      // Erro ao salvar pet
      // Mostrar erro ao usuário
      await showConfirmation({
        title: 'Erro',
            message: `Erro ao salvar pet: ${error.message || 'Erro desconhecido'}`,
        confirmText: 'OK',
        cancelText: '',
        type: 'danger'
      });
    }
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    setPetFieldErrors({});
    setFormData({
      nome: pet.nome,
      raca: pet.raca || '',
      mesNascimento: pet.mesNascimento || '',
      anoNascimento: pet.anoNascimento != null && pet.anoNascimento !== '' ? String(pet.anoNascimento) : '',
      porte: pet.porte || '',
      pelagem: pet.pelagem || '',
      tutor: pet.tutor || '',
      telefone: pet.telefone || '',
      endereco: pet.endereco || '',
      observacoes: pet.observacoes || '',
      fonteContato: pet.fonteContato || '',
      temAlergia: pet.temAlergia || false,
      detalhesAlergia: pet.detalhesAlergia || '',
      temDorArticular: pet.temDorArticular || false,
      detalhesDorArticular: pet.detalhesDorArticular || '',
      usaMedicamento: pet.usaMedicamento || false,
      detalhesMedicamento: pet.detalhesMedicamento || '',
      acompanhamentoVeterinario: pet.acompanhamentoVeterinario || false,
      detalhesAcompanhamento: pet.detalhesAcompanhamento || '',
      autorizaImagem: pet.autorizaImagem || false
    });
    setShowModal(true);
  };

  const handleDelete = async (idPet) => {
    // Iniciando exclusão para pet
    
    const confirmed = await showConfirmation({
      title: 'Excluir Pet',
      message: 'Tem certeza que deseja excluir este pet? Esta ação não pode ser desfeita e todos os agendamentos relacionados serão excluídos.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger'
    });

    if (!confirmed) {
      // Usuário cancelou a exclusão
      return;
    }

    // Usuário confirmou, prosseguindo com exclusão
    
    try {
      // Buscar todos os agendamentos relacionados ao pet
      const appointmentsQuery = query(
        collection(db, 'agendamentos'),
        where('idPet', '==', idPet)
      );
      const appointmentsSnapshot = await getDocs(appointmentsQuery);
      
      // Deletar todos os agendamentos relacionados
      const deletePromises = [];
      appointmentsSnapshot.forEach((appointmentDoc) => {
        deletePromises.push(deleteDoc(doc(db, 'agendamentos', appointmentDoc.id)));
      });
      
      // Aguardar todas as exclusões de agendamentos
      await Promise.all(deletePromises);
      
      // Deletar o pet
      await deleteDoc(doc(db, 'pets', idPet));
      
      // Atualizar agregado
      // Atualizando agregado na exclusão
      await updatePetsAggregationOnDelete(idPet);
      // Agregado atualizado com sucesso na exclusão
      // O listener em tempo real atualizará automaticamente
      
      // Remover o pet do estado local imediatamente
      // Marcar como atualização manual para evitar que o useEffect sobrescreva
      manualUpdateRef.current = true;
      setPets(prev => prev.filter(p => p.id !== idPet));
    } catch (error) {
      // Erro ao excluir pet
      // Mostrar erro ao usuário
      await showConfirmation({
        title: 'Erro',
        message: `Erro ao excluir pet: ${error.message || 'Erro desconhecido'}`,
        confirmText: 'OK',
        cancelText: '',
        type: 'danger'
      });
    }
  };



  // Função para filtrar pets baseado no termo de pesquisa e filtro de plano
  // Nota: Como a busca de texto não é suportada pelo Firestore, aplicamos os filtros no cliente
  // mas apenas nos pets já carregados da página atual
  const filteredPets = pets.filter((pet) => {
    // Filtro de plano
    let planMatch = true;
    if (planFilter === 'with-plan') {
      planMatch = pet.planoMensal === true || pet.planoMensal === 'true';
    } else if (planFilter === 'plan-ended') {
      // Plano encerrado: planoMensal explicitamente false
      planMatch = pet.planoMensal === false || pet.planoMensal === 'false';
    } else if (planFilter === 'without-plan') {
      // Sem plano: planoMensal não está definido ou é null/undefined, e não tem diasAgendados
      planMatch = (pet.planoMensal !== true && pet.planoMensal !== 'true' && pet.planoMensal !== false && pet.planoMensal !== 'false') && 
                  (!pet.diasAgendados || pet.diasAgendados.length === 0);
    }

    // Filtro de imagens: sem campo galeria = sem imagem; com campo galeria = com imagem
    let imageMatch = true;
    const temGaleria = pet.galeria != null;
    if (imageFilter === 'with-images') {
      imageMatch = temGaleria;
    } else if (imageFilter === 'without-images') {
      imageMatch = !temGaleria;
    }
    
    // Filtro de pesquisa
    if (!searchTerm) return planMatch && imageMatch;
    
    const searchLower = searchTerm.toLowerCase();
    const name = (pet.nome || '').toLowerCase();
    const breed = (pet.raca || '').toLowerCase();
    const owner = (pet.tutor || '').toLowerCase();
    const phone = (pet.telefone || '').toLowerCase();
    const address = (pet.endereco || '').toLowerCase();
    const age = pet.mesNascimento && pet.anoNascimento 
      ? `${pet.mesNascimento}/${pet.anoNascimento}`.toLowerCase()
      : '';
    const sizeLabel = getSizeLabel(pet.porte || '').toLowerCase();
    const coatLabel = getCoatLabel(pet.pelagem || '').toLowerCase();
    const notes = (pet.observacoes || '').toLowerCase();
    
    const searchMatch = name.includes(searchLower) || 
           breed.includes(searchLower) || 
           owner.includes(searchLower) || 
           phone.includes(searchLower) || 
           address.includes(searchLower) ||
           age.includes(searchLower) ||
           sizeLabel.includes(searchLower) ||
           coatLabel.includes(searchLower) ||
           notes.includes(searchLower);
    
    return planMatch && imageMatch && searchMatch;
  });

  // Ordenar pets filtrados: priorizar nome do pet na busca, depois ordem alfabética (A-Z ou Z-A)
  const sortedPets = [...filteredPets].sort((a, b) => {
    const nameA = (a.nome || '').toUpperCase();
    const nameB = (b.nome || '').toUpperCase();
    
    // Se há termo de busca, priorizar pets cujo nome corresponde
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const nameAMatches = nameA.toLowerCase().includes(searchLower);
      const nameBMatches = nameB.toLowerCase().includes(searchLower);
      
      // Se apenas um corresponde ao nome, ele vem primeiro
      if (nameAMatches && !nameBMatches) return -1;
      if (!nameAMatches && nameBMatches) return 1;
      
      // Se ambos correspondem ou nenhum corresponde, ordenar alfabeticamente
      const comparison = nameA.localeCompare(nameB, 'pt-BR');
      return sortOrder === 'desc' ? -comparison : comparison;
    }
    
    // Sem busca, apenas ordem alfabética (A-Z ou Z-A)
    const comparison = nameA.localeCompare(nameB, 'pt-BR');
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  // Paginação no cliente (apenas visual)
  const totalPages = Math.ceil(sortedPets.length / petsPerPage);
  const startIndex = (currentPage - 1) * petsPerPage;
  const endIndex = startIndex + petsPerPage;
  const paginatedPets = sortedPets.slice(startIndex, endIndex);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  
  // Funções de navegação de página
  const loadNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const loadPrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground-light dark:text-foreground-dark flex items-center space-x-2">
            <Dog className="w-6 h-6" />
            <span>Pets</span>
          </h1>
          <p className="hidden sm:block text-muted-light dark:text-muted-dark">Gerencie os pets cadastrados</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              setPetFieldErrors({});
              setShowModal(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Novo Pet</span>
            <span className="sm:hidden">Novo</span>
          </button>
        </div>
      </div>

      {/* Barra de Pesquisa e Filtros: mobile em grid (pesquisa | A-Z, depois filtros); desktop em uma linha com A-Z à direita */}
      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-2 gap-y-3 sm:flex sm:flex-row sm:items-center sm:gap-4">
        <div className="relative col-start-1 row-start-1 min-w-0 w-full sm:order-1 sm:min-w-0 sm:flex-1 sm:basis-0">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-muted-light dark:text-muted-dark" />
          <input
            type="text"
            placeholder="Pesquisar"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`input-field w-full pl-10 ${searchTerm ? 'pr-10' : ''}`}
            data-desktop-placeholder="Pesquisar pets por nome, raça, tutor, telefone, endereço, porte, pelagem..."
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 transform text-muted-light transition-colors hover:text-foreground-light dark:text-muted-dark dark:hover:text-foreground-dark"
              title="Limpar pesquisa"
            >
              ×
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
          }}
          className="btn-secondary col-start-2 row-start-1 flex shrink-0 items-center space-x-2 self-center whitespace-nowrap sm:order-3"
          title={sortOrder === 'asc' ? 'Ordenar Z-A' : 'Ordenar A-Z'}
        >
          <ArrowUpDown className="h-4 w-4" />
          <span>{sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
        </button>

        {/* Filtros Plano + Imagem */}
        <div className="col-span-2 col-start-1 row-start-2 flex min-w-0 flex-row flex-wrap items-center gap-2 sm:order-2 sm:col-auto sm:row-auto sm:w-auto sm:shrink-0 sm:flex-none sm:flex-nowrap sm:justify-end sm:gap-4">
          {/* Filtro de Plano */}
          <div className="flex min-w-0 flex-1 flex-row items-center gap-1.5 sm:w-44 sm:flex-none sm:gap-2">
            <span
              id="pets-list-plan-filter-caption"
              className="flex shrink-0 cursor-default items-center text-foreground-light dark:text-foreground-dark"
              title="Plano"
            >
              <CreditCard className="h-5 w-5 text-muted-light dark:text-muted-dark" aria-hidden />
              <span className="sr-only">Plano</span>
            </span>
            <div className="min-w-0 flex-1 sm:min-w-0 sm:flex-1">
              <MenuDropdown
                id="pets-list-plan-filter"
                name="planFilter"
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                options={PET_LIST_PLAN_FILTER_OPTIONS}
                placeholder="Todos"
                ariaLabelledBy="pets-list-plan-filter-caption"
              />
            </div>
          </div>

          {/* Filtro de Imagens */}
          <div className="flex min-w-0 flex-1 flex-row items-center gap-1.5 sm:w-44 sm:flex-none sm:gap-2">
            <span
              id="pets-list-image-filter-caption"
              className="flex shrink-0 cursor-default items-center text-foreground-light dark:text-foreground-dark"
              title="Imagem"
            >
              <Image className="h-5 w-5 text-muted-light dark:text-muted-dark" aria-hidden />
              <span className="sr-only">Imagem</span>
            </span>
            <div className="min-w-0 flex-1 sm:min-w-0 sm:flex-1">
              <MenuDropdown
                id="pets-list-image-filter"
                name="imageFilter"
                value={imageFilter}
                onChange={(e) => setImageFilter(e.target.value)}
                options={PET_LIST_IMAGE_FILTER_OPTIONS}
                placeholder="Todos"
                ariaLabelledBy="pets-list-image-filter-caption"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Grid de Pets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
        {paginatedPets.map((pet) => (
          <div
            key={pet.id}
            className="card hover:shadow-md transition-shadow cursor-pointer p-4"
            onClick={() => navigate(`/pets/${pet.id}`)}
            role="button"
            tabIndex={0}
          >
            <div className="flex-1">
              <div className="flex items-center space-x-1.5">
                <Dog className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <h3 className="font-semibold text-foreground-light dark:text-foreground-dark">{pet.nome}</h3>
              </div>
              <div className="text-sm text-muted-light dark:text-muted-dark mt-1">
                <p className="flex items-center space-x-1.5">
                  <Tag className="w-3.5 h-3.5 text-muted-light dark:text-muted-dark flex-shrink-0" />
                  <span><strong>Raça:</strong> {pet.raca || 'Não informada'}</span>
                </p>
                <p className="flex items-center space-x-1.5">
                  <User className="w-3.5 h-3.5 text-muted-light dark:text-muted-dark flex-shrink-0" />
                  <span><strong>Tutor:</strong> {formatName(pet.tutor) || 'Não informado'}</span>
                </p>
                <div className="mt-1.5 flex justify-end">
                  {pet.planoMensal === true ? (
                    <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 text-xs font-medium rounded-full">
                      <Calendar className="w-3 h-3" />
                      <span>Plano Ativo</span>
                    </span>
                  ) : pet.planoMensal === false ? (
                    <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 text-xs font-medium rounded-full">
                      <Calendar className="w-3 h-3" />
                      <span>Plano Encerrado</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-300 text-xs font-medium rounded-full">
                      <Calendar className="w-3 h-3" />
                      <span>Sem Plano</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Controles de Paginação */}
      {(!loading && sortedPets.length > petsPerPage && (hasNextPage || hasPrevPage || currentPage > 1)) && (
        <div className="flex items-center justify-center mt-6 pt-4 border-t border-border-light dark:border-border-dark">
          <div className="flex items-center space-x-2">
            <button
              onClick={loadPrevPage}
              disabled={!hasPrevPage || loading}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasPrevPage && !loading
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Anterior</span>
            </button>
            
            <span className="text-sm text-muted-light dark:text-muted-dark">
              Página {currentPage} de {totalPages} ({sortedPets.length} pets)
            </span>
            
            <button
              onClick={loadNextPage}
              disabled={!hasNextPage || loading}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasNextPage && !loading
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span>Próxima</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {!loading && pets.length === 0 && sortedPets.length === 0 && (
        <div className="text-center py-12">
          <Dog className="w-16 h-16 text-muted-light dark:text-muted-dark mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground-light dark:text-foreground-dark mb-2">Nenhum pet cadastrado</h3>
          <p className="text-muted-light dark:text-muted-dark mb-4">Comece adicionando seu primeiro pet</p>
          <button
            onClick={() => {
              setPetFieldErrors({});
              setShowModal(true);
            }}
            className="btn-primary"
          >
            Adicionar Primeiro Pet
          </button>
        </div>
      )}

      {!loading && pets.length > 0 && sortedPets.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-16 h-16 text-muted-light dark:text-muted-dark mx-auto mb-4" />
          <p className="text-muted-light dark:text-muted-dark mb-4">Tente ajustar os filtros ou pesquisar com outros termos</p>
          <div className="flex space-x-2 justify-center">
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="btn-secondary"
              >
                Limpar Pesquisa
              </button>
            )}
            {(planFilter !== 'all' || imageFilter !== 'all') && (
              <button
                onClick={() => {
                  setPlanFilter('all');
                  setImageFilter('all');
                }}
                className="btn-secondary"
              >
                Mostrar Todos
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal de Adicionar/Editar Pet (layout alinhado ao Agenda / WhatsApp) */}
      {showModal && (
        <Portal>
          <div
            className="fixed inset-0 z-[99999] flex items-stretch justify-center bg-black bg-opacity-50 p-0 sm:items-center sm:p-4"
            data-modal="pet"
            onClick={(e) => {
              if (e.target === e.currentTarget) closePetModal();
            }}
          >
            <div
              ref={petModalSheetRef}
              className="relative flex h-dvh max-h-dvh min-h-0 w-full flex-col overflow-hidden bg-card-light dark:bg-card-dark sm:h-auto sm:min-h-0 sm:max-h-[90vh] sm:max-w-4xl sm:rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative z-50 flex shrink-0 items-center justify-between gap-2 border-b border-border-light bg-card-light px-4 py-4 dark:border-border-dark dark:bg-card-dark sm:px-6">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Dog className="h-5 w-5 shrink-0 text-foreground-light dark:text-foreground-dark" />
                  <h2 className="min-w-0 text-xl font-bold text-foreground-light dark:text-foreground-dark">
                    {editingPet ? 'Editar Pet' : 'Novo Pet'}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closePetModal}
                  className="shrink-0 p-1 text-muted-light transition-colors hover:text-foreground-light dark:text-muted-dark dark:hover:text-foreground-dark"
                  aria-label="Fechar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                onSubmit={handleSubmit}
                className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6"
                style={{ overscrollBehavior: 'contain' }}
                noValidate
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="lg:col-span-1">
                    <span id="pets-modal-nome-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Nome do Pet
                    </span>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => {
                        const processedValue = e.target.value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
                        handlePetFormChange({ nome: processedValue });
                      }}
                      aria-labelledby="pets-modal-nome-caption"
                      className={`input-field !py-1.5 uppercase ${petFieldErrors.nome ? '!border-red-500 dark:!border-red-500' : ''}`}
                      placeholder="Ex: Rex"
                      style={{ textTransform: 'uppercase' }}
                      required
                    />
                    {petFieldErrors.nome && (
                      <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
                    )}
                  </div>

                  <div className="lg:col-span-1">
                    <span id="pets-modal-raca-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Raça
                    </span>
                    <input
                      type="text"
                      value={formData.raca}
                      onChange={(e) => {
                        const processedValue = e.target.value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
                        handlePetFormChange({ raca: processedValue });
                      }}
                      aria-labelledby="pets-modal-raca-caption"
                      className="input-field !py-1.5 uppercase"
                      placeholder="Ex: Golden"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>

                  <div className="lg:col-span-1">
                    <span id="pets-modal-nascimento-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Data de nascimento (aproximadamente)
                    </span>
                    <div className="grid grid-cols-2 gap-3">
                      <MenuDropdown
                        id="pets-modal-mesNascimento"
                        name="mesNascimento"
                        value={formData.mesNascimento}
                        onChange={handlePetMenuChange}
                        options={PET_MODAL_MONTH_OPTIONS}
                        placeholder="Mês"
                        portalRootRef={petModalSheetRef}
                        ariaLabelledBy="pets-modal-nascimento-caption"
                      />
                      <MenuDropdown
                        id="pets-modal-anoNascimento"
                        name="anoNascimento"
                        value={formData.anoNascimento !== '' && formData.anoNascimento != null ? String(formData.anoNascimento) : ''}
                        onChange={handlePetMenuChange}
                        options={petModalYearOptions}
                        placeholder="Ano"
                        portalRootRef={petModalSheetRef}
                        ariaLabelledBy="pets-modal-nascimento-caption"
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-1">
                    <span id="pets-modal-porte-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Porte
                    </span>
                    <MenuDropdown
                      id="pets-modal-porte"
                      name="porte"
                      value={formData.porte}
                      onChange={handlePetMenuChange}
                      options={PET_MODAL_PORTE_OPTIONS}
                      placeholder="Selecione o porte"
                      portalRootRef={petModalSheetRef}
                      ariaLabelledBy="pets-modal-porte-caption"
                    />
                  </div>

                  <div className="lg:col-span-1">
                    <span id="pets-modal-pelagem-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Pelagem
                    </span>
                    <MenuDropdown
                      id="pets-modal-pelagem"
                      name="pelagem"
                      value={formData.pelagem}
                      onChange={handlePetMenuChange}
                      options={PET_MODAL_PELAGEM_OPTIONS}
                      placeholder="Selecione a pelagem"
                      portalRootRef={petModalSheetRef}
                      ariaLabelledBy="pets-modal-pelagem-caption"
                    />
                  </div>

                  <div className="lg:col-span-1">
                    <span id="pets-modal-tutor-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Nome do Tutor
                    </span>
                    <input
                      type="text"
                      value={formData.tutor}
                      onChange={(e) => {
                        const processedValue = e.target.value.replace(/[^a-zA-ZáàâãéèêíìîóòôõúùûçÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ\s]/g, '');
                        handlePetFormChange({ tutor: processedValue });
                      }}
                      aria-labelledby="pets-modal-tutor-caption"
                      className={`input-field !py-1.5 uppercase ${petFieldErrors.tutor ? '!border-red-500 dark:!border-red-500' : ''}`}
                      placeholder="Ex: João Silva"
                      style={{ textTransform: 'uppercase' }}
                    />
                    {petFieldErrors.tutor && (
                      <span className="mt-1 block text-xs text-red-500">Preencha este campo</span>
                    )}
                  </div>

                  <div className="lg:col-span-1">
                    <span id="pets-modal-telefone-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Telefone
                    </span>
                    <input
                      type="tel"
                      value={formatPhone(formData.telefone)}
                      onChange={(e) => {
                        const numbers = e.target.value.replace(/\D/g, '').slice(0, 11);
                        handlePetFormChange({ telefone: numbers });
                      }}
                      aria-labelledby="pets-modal-telefone-caption"
                      className={`input-field !py-1.5 ${petFieldErrors.telefone ? '!border-red-500 dark:!border-red-500' : ''}`}
                      placeholder="Ex: (41) 99999-9999"
                    />
                    {petFieldErrors.telefone && (
                      <span className="mt-1 block text-xs text-red-500">Preencha com um telefone válido (mín. 10 dígitos)</span>
                    )}
                  </div>

                  <div className="lg:col-span-1">
                    <span id="pets-modal-fonte-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Como nos conheceu?
                    </span>
                    <input
                      type="text"
                      value={formData.fonteContato}
                      onChange={(e) => handlePetFormChange({ fonteContato: e.target.value })}
                      aria-labelledby="pets-modal-fonte-caption"
                      className="input-field !py-1.5"
                      placeholder="Ex: Instagram, indicação, etc."
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <span id="pets-modal-endereco-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                      Endereço
                    </span>
                    <input
                      type="text"
                      value={formData.endereco}
                      onChange={(e) => handlePetFormChange({ endereco: formatName(e.target.value) })}
                      aria-labelledby="pets-modal-endereco-caption"
                      className="input-field !py-1.5"
                      placeholder="Ex: Rua Exemplo, 123 - Centro"
                    />
                  </div>
                </div>

                <div>
                  <span id="pets-modal-saude-caption" className="mb-2 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    Informações de Saúde do Pet:
                  </span>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.temAlergia}
                          onChange={(e) =>
                            handlePetFormChange({
                              temAlergia: e.target.checked,
                              detalhesAlergia: e.target.checked ? formData.detalhesAlergia : ''
                            })
                          }
                          aria-label="Tem alergia"
                          className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Tem alergia</span>
                      </div>
                      {formData.temAlergia && (
                        <input
                          type="text"
                          value={formData.detalhesAlergia}
                          onChange={(e) => handlePetFormChange({ detalhesAlergia: e.target.value })}
                          className="input-field !py-1.5 text-sm"
                          placeholder="Descreva a alergia específica..."
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.temDorArticular}
                          onChange={(e) =>
                            handlePetFormChange({
                              temDorArticular: e.target.checked,
                              detalhesDorArticular: e.target.checked ? formData.detalhesDorArticular : ''
                            })
                          }
                          aria-label="Tem dor articular"
                          className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Tem dor articular</span>
                      </div>
                      {formData.temDorArticular && (
                        <input
                          type="text"
                          value={formData.detalhesDorArticular}
                          onChange={(e) => handlePetFormChange({ detalhesDorArticular: e.target.value })}
                          className="input-field !py-1.5 text-sm"
                          placeholder="Descreva o problema articular específico..."
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.usaMedicamento}
                          onChange={(e) =>
                            handlePetFormChange({
                              usaMedicamento: e.target.checked,
                              detalhesMedicamento: e.target.checked ? formData.detalhesMedicamento : ''
                            })
                          }
                          aria-label="Usa medicamento"
                          className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">Usa medicamento</span>
                      </div>
                      {formData.usaMedicamento && (
                        <input
                          type="text"
                          value={formData.detalhesMedicamento}
                          onChange={(e) => handlePetFormChange({ detalhesMedicamento: e.target.value })}
                          className="input-field !py-1.5 text-sm"
                          placeholder="Descreva o medicamento usado..."
                        />
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.acompanhamentoVeterinario}
                          onChange={(e) =>
                            handlePetFormChange({
                              acompanhamentoVeterinario: e.target.checked,
                              detalhesAcompanhamento: e.target.checked ? formData.detalhesAcompanhamento : ''
                            })
                          }
                          aria-label="Está fazendo acompanhamento veterinário"
                          className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">
                          Está fazendo acompanhamento veterinário
                        </span>
                      </div>
                      {formData.acompanhamentoVeterinario && (
                        <input
                          type="text"
                          value={formData.detalhesAcompanhamento}
                          onChange={(e) => handlePetFormChange({ detalhesAcompanhamento: e.target.value })}
                          className="input-field !py-1.5 text-sm"
                          placeholder="Descreva o tipo de acompanhamento..."
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.autorizaImagem}
                    onChange={(e) => handlePetFormChange({ autorizaImagem: e.target.checked })}
                    aria-label="Autorizo o uso de imagem do pet nas redes sociais"
                    className="h-5 w-5 shrink-0 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="cursor-default text-sm text-foreground-light dark:text-foreground-dark">
                    Autorizo o uso de imagem do pet nas redes sociais
                  </span>
                </div>

                <div>
                  <span id="pets-modal-observacoes-caption" className="mb-1 block cursor-default text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    Observações
                  </span>
                  <textarea
                    ref={petObservationsRef}
                    value={formData.observacoes}
                    onChange={(e) => handlePetFormChange({ observacoes: e.target.value })}
                    aria-labelledby="pets-modal-observacoes-caption"
                    className="input-field !py-1.5 min-h-[4.5rem] resize-none overflow-hidden"
                    placeholder="Ex: Pet adora brincar com bolinhas..."
                    rows={1}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={closePetModal} className="btn-secondary flex-1">
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    {editingPet ? 'Salvar' : 'Adicionar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Modal de Confirmação */}
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmText={confirmation.confirmText}
        cancelText={confirmation.cancelText}
        type={confirmation.type}
      />
    </div>
  );
};

export default Pets;
